import { useEffect, useRef, useState } from "react";
import { HOUSE_BOTS, standings as tableOf, type Replay } from "@buildlab/shared/arena";
import { arenaApi, type ArenaClassPick, type ArenaMatchInfo, type ArenaRow, type ArenaStandings, type ArenaTeacherEntry } from "./api";
import { runTournament, type PlayedMatch, type TournamentBot } from "./arena-runner";
import { ArenaReplay } from "./ArenaReplay";
import { ArenaMatchList, ArenaTable } from "./BotArena";
import { Back } from "./icons";
import { Avatar } from "./Sprite";
import "./Arena.css";

// The teacher's Bot Arena page (#arena, decision 99): pick a class (demo classes apart, like golf
// boards), look over the entered bots (screening holds some back for you), and run a round robin
// in this browser. Each bot plays from its own sandboxed frame (arena-runner.ts); the progress and
// the table update as matches finish, then the moves go to the server, which scores them again
// and keeps the replays. Watch any match big on the projector.

type Run = { state: "idle" } | { state: "running"; done: number; total: number; last: string } | { state: "saving" } | { state: "error"; message: string };

export function ArenaPage({ onExit }: { onExit: () => void }) {
  const [classId, setClassId] = useState<string | undefined>(undefined);
  const [classes, setClasses] = useState<ArenaClassPick[]>([]);
  const [className, setClassName] = useState("");
  const [entries, setEntries] = useState<ArenaTeacherEntry[] | null>(null);
  const [standings, setStandings] = useState<ArenaStandings | null>(null);
  const [house, setHouse] = useState<string[]>([]);
  const [run, setRun] = useState<Run>({ state: "idle" });
  const [live, setLive] = useState<{ table: ArenaRow[]; matches: (ArenaMatchInfo & { replay: Replay })[] } | null>(null);
  const [watch, setWatch] = useState<{ id?: string; given?: ArenaMatchInfo & { replay: Replay } } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const host = useRef<HTMLDivElement>(null);
  const stop = useRef(false);

  const load = async (id: string | undefined) => {
    try {
      const e = await arenaApi.entries(id);
      setClasses(e.classes);
      setClassName(e.className);
      setEntries(e.entries);
      setStandings(await arenaApi.standings(e.classId));
      if (id === undefined) setClassId(e.classId);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  useEffect(() => {
    void load(classId);
  }, [classId]);
  useEffect(() => () => void (stop.current = true), []);

  const playing = (entries ?? []).filter((e) => e.status === "ok");
  const held = (entries ?? []).filter((e) => e.status === "held");
  const bots: TournamentBot[] = [...playing.map((e) => ({ id: e.id, code: e.code })), ...HOUSE_BOTS.filter((h) => house.includes(h.id)).map((h) => ({ id: h.id, code: h.code }))];
  const busy = run.state === "running" || run.state === "saving";

  /** How a bot shows in the live table and matches before the server has them. */
  const sideOf = (id: string) => {
    const e = playing.find((x) => x.id === id);
    const h = HOUSE_BOTS.find((x) => x.id === id);
    return { name: e ? e.name : "House bot", avatar: e?.avatar ?? null, botName: e?.botName ?? h?.name ?? "?", house: !e, me: false };
  };

  const start = async () => {
    if (!host.current || bots.length < 2 || classId === undefined) return;
    stop.current = false;
    const seed = 1 + Math.floor(Math.random() * 2_000_000_000);
    const finished: PlayedMatch[] = [];
    setLive({ table: [], matches: [] });
    setRun({ state: "running", done: 0, total: 0, last: "" });
    try {
      const matches = await runTournament({
        bots,
        seed,
        host: host.current,
        cancelled: () => stop.current,
        onMatch: (done, total, m) => {
          finished.push(m);
          const a = sideOf(m.a);
          const b = sideOf(m.b);
          setRun({ state: "running", done, total, last: `${a.botName} ${m.aCoins}–${m.bCoins} ${b.botName}` });
          const table = tableOf(
            bots.map((x) => x.id),
            finished.map((x) => ({ a: x.a, b: x.b, aCoins: x.aCoins, bCoins: x.bCoins })),
          ).map((r) => ({ ...sideOf(r.id), rank: r.rank, played: r.played, wins: r.wins, draws: r.draws, losses: r.losses, coins: r.coins, points: r.points }));
          setLive({ table, matches: finished.map((x, i) => ({ id: `live-${i}`, n: i + 1, a: sideOf(x.a), b: sideOf(x.b), aCoins: x.aCoins, bCoins: x.bCoins, winner: x.winner, replay: x.replay })) });
        },
      });
      setRun({ state: "saving" });
      await arenaApi.saveTournament({ classId, seed, bots: bots.map((b) => b.id), matches: matches.map((m) => ({ a: m.a, b: m.b, replay: m.replay })) });
      setLive(null);
      setRun({ state: "idle" });
      setStandings(await arenaApi.standings(classId));
    } catch (e) {
      setRun({ state: "error", message: (e as Error).message });
    }
  };

  const shown = live ?? (standings?.tournament ? { table: standings.table, matches: standings.matches } : null);
  return (
    <div className="arena-page">
      <header className="topbar">
        <button type="button" className="pill-dark back" title="Back to Home" onClick={onExit}>
          <Back />
          <span className="bar-label">Home</span>
        </button>
        <div className="title">
          <span className="golf-flag" aria-hidden="true">
            🤖
          </span>
          <span className="eyebrow gold">Bot Arena · Coin Grab</span>
          <h1>Tournaments</h1>
        </div>
        <label className="arena-class-pick">
          <span className="eyebrow gold">Class</span>
          <select value={classId ?? ""} disabled={busy} onChange={(e) => setClassId(e.target.value)} aria-label="Class">
            {classes.map((k) => (
              <option key={k.id || "teachers"} value={k.id}>
                {k.name}
                {k.demo ? " (demo)" : ""}
              </option>
            ))}
          </select>
        </label>
      </header>
      {error && <p className="page-error">{error}</p>}
      <div className="arena-cols">
        <section className="arena-panel" aria-label="Entered bots">
          <h2>
            Entered bots <span className="muted small">{entries ? `${playing.length} in ${className}` : ""}</span>
          </h2>
          {entries && entries.length === 0 && <p className="small muted">No bots entered in {className} yet. Students press Enter my bot in their bot's workspace.</p>}
          <ul className="arena-entries">
            {[...held, ...playing].map((e) => (
              <li key={e.id} className={e.status === "held" ? "held" : undefined}>
                <Avatar name={e.name} avatar={e.avatar} size={16} />
                <span className="arena-entry-who">
                  <b>{e.botName}</b>
                  <span className="small muted">
                    {e.name} · {new Date(e.updatedAt).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" })}
                  </span>
                  {e.screening && <span className="small arena-held">{e.screening}</span>}
                </span>
                <span className="row">
                  <button type="button" className="linklike small" onClick={() => setCode(code === e.id ? null : e.id)} aria-expanded={code === e.id}>
                    Code
                  </button>
                  {e.status === "held" && (
                    <button type="button" className="outline small" disabled={busy} onClick={() => void arenaApi.approve(e.id).then(() => load(classId))}>
                      Let it play
                    </button>
                  )}
                  <button
                    type="button"
                    className="linklike small"
                    disabled={busy}
                    onClick={() => window.confirm(`Take ${e.botName} out of the arena? ${e.name} can enter again.`) && void arenaApi.remove(e.id).then(() => load(classId))}
                  >
                    Remove
                  </button>
                </span>
                {code === e.id && <pre className="arena-code">{e.code}</pre>}
              </li>
            ))}
          </ul>
          <fieldset className="arena-house" disabled={busy}>
            <legend className="small">House bots (good for a small class)</legend>
            {HOUSE_BOTS.map((h) => (
              <label key={h.id} className="small">
                <input type="checkbox" checked={house.includes(h.id)} onChange={(e) => setHouse(e.target.checked ? [...house, h.id] : house.filter((x) => x !== h.id))} /> <b>{h.name}</b>: {h.about}
              </label>
            ))}
          </fieldset>
          <div className="arena-run">
            <button type="button" className="primary" disabled={busy || bots.length < 2} onClick={() => void start()}>
              {busy ? "Running…" : "Run tournament"}
            </button>
            <span className="small muted">
              {bots.length < 2 ? "Needs at least 2 bots." : `${bots.length} bots · ${bots.length * (bots.length - 1)} matches, played here in this browser.`}
            </span>
          </div>
          {run.state === "running" && (
            <div className="arena-progress" role="status">
              <div className="arena-bar" aria-hidden="true">
                <span style={{ width: `${run.total ? (100 * run.done) / run.total : 0}%` }} />
              </div>
              <p className="small">
                Match {run.done} of {run.total || "…"}
                {run.last ? ` · ${run.last}` : ""}
              </p>
              <button type="button" className="linklike small" onClick={() => (stop.current = true)}>
                Stop
              </button>
            </div>
          )}
          {run.state === "saving" && <p className="small" role="status">Saving the results…</p>}
          {run.state === "error" && <p className="page-error small">{run.message}</p>}
          {/* The bots' sandboxed frames live here while they play: off screen, never in the app's origin. */}
          <div ref={host} className="arena-frames" aria-hidden="true" />
        </section>

        <section className="arena-panel arena-results" aria-label="Standings">
          <h2>
            {live ? "Live standings" : "Latest tournament"}{" "}
            {!live && standings?.tournament && <span className="muted small">{new Date(standings.tournament.createdAt).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>}
          </h2>
          {!shown && <p className="golf-board-empty">No tournament yet in {className}.</p>}
          {shown && shown.table.length > 0 && <ArenaTable table={shown.table} />}
          {shown && shown.matches.length > 0 && (
            <>
              <h3 className="arena-sub">Matches</h3>
              <ArenaMatchList matches={shown.matches} onWatch={(m) => setWatch(live ? { given: m as ArenaMatchInfo & { replay: Replay } } : { id: m.id })} />
            </>
          )}
        </section>
      </div>
      {watch && <ArenaReplay matchId={watch.id} given={watch.given} onClose={() => setWatch(null)} />}
    </div>
  );
}
